import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box
} from "@material-ui/core";
import { CREATIVE_EDIT_COSTS } from "../../../@types/sharedSchema";

interface TokensExplainerModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Explains what tokens are and how they're spent. Opened from the help icon next to
 * the token balance on the creatives page.
 */
export default function TokensExplainerModal({
  open,
  onClose
}: TokensExplainerModalProps): JSX.Element {
  const { t } = useTranslation();
  const socialCost = CREATIVE_EDIT_COSTS.socialMedia;

  const points: { emoji: string; text: string }[] = [
    {
      emoji: "🪙",
      text: t(
        "creativesGenerator.coins.explainer.whatAreThey",
        "טוקנים הם המטבע שבו משתמשים ליצירה ולעריכה של קריאטיבים בעזרת AI."
      )
    },
    {
      emoji: "🎨",
      text: t("creativesGenerator.coins.explainer.cost", {
        defaultValue: "כל יצירת קריאטיב למדיה חברתית עולה {{cost}} טוקנים.",
        cost: socialCost
      })
    },
    {
      emoji: "🗓️",
      text: t(
        "creativesGenerator.coins.explainer.monthly",
        "מנויים מקבלים מכסת טוקנים חודשית. טוקנים חודשיים שלא נוצלו פגים בסוף החודש."
      )
    },
    {
      emoji: "🛒",
      text: t(
        "creativesGenerator.coins.explainer.buy",
        "תמיד אפשר לרכוש חבילות טוקנים נוספות. טוקנים שנרכשו נשארים אצלכם לתמיד — אין להם תאריך תפוגה."
      )
    },
    {
      emoji: "🏢",
      text: t(
        "creativesGenerator.coins.explainer.org",
        "ניתן לרכוש טוקנים גם ברמת המשרד, כך שכל חברי הצוות יכולים להשתמש באותו מאגר טוקנים."
      )
    },
    {
      emoji: "⛔",
      text: t(
        "creativesGenerator.coins.explainer.insufficient",
        "אם אין מספיק טוקנים ביתרה, לא ניתן ליצור קריאטיב חדש עד לרכישת טוקנים נוספים."
      )
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("creativesGenerator.coins.explainer.title", "מה הם טוקנים?")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {points.map((point, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box component="span" sx={{ fontSize: 20, lineHeight: 1.4 }} aria-hidden>
                {point.emoji}
              </Box>
              <Typography variant="body2">{point.text}</Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t("common.gotIt", "הבנתי")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
